import { addYears, format, parseISO } from 'date-fns';
import type { SharedDvlaVehicleLookupResult } from './home';

type FirstRegistrationMonth = {
  year: number;
  month: number;
};

export type SharedVehicleTaxEstimate = {
  annual: number | null;
  sixMonth: number | null;
  monthlyDirectDebitTotal: number | null;
  monthlyDirectDebitInstallment: number | null;
  basis: 'post-2017-standard' | '2001-2017-co2' | 'pre-2001-engine' | 'unknown';
  note: string | null;
};

export type SharedHomeVehicleRecord = {
  id?: string | null;
  registrationNumber: string;
  make: string | null;
  motDueDate: string | null;
  motStatus: string | null;
  taxDueDate: string | null;
  taxStatus: string | null;
  taxAnnualCost: number | null;
  taxSixMonthCost: number | null;
  taxMonthlyDirectDebitTotal: number | null;
  taxMonthlyDirectDebitInstallment: number | null;
  motEstimatedCost: number | null;
  serviceLastDate: string | null;
  serviceDueDate: string | null;
  dvlaSyncedAt: string | null;
};

export type SharedVehicleInsuranceRenewal = {
  type: string;
  renewalDate: string;
  registrationNumber?: string | null;
  vehicleLabel?: string | null;
  provider?: string | null;
};

const POST_2017_STANDARD_RATES = {
  annual: 200,
  sixMonth: 110,
  monthlyDirectDebitTotal: 210,
  monthlyDirectDebitInstallment: 17.5,
} as const;

const PRE_2001_ENGINE_RATES = {
  under1550: {
    annual: 230,
    sixMonth: 126.5,
    monthlyDirectDebitTotal: 241.5,
    monthlyDirectDebitInstallment: 20.125,
  },
  over1550: {
    annual: 375,
    sixMonth: 206.25,
    monthlyDirectDebitTotal: 393.75,
    monthlyDirectDebitInstallment: 32.8125,
  },
} as const;

const CO2_RATES_2001_TO_2017 = [
  { max: 100, annual: 20, sixMonth: null, monthlyDirectDebitTotal: 21, monthlyDirectDebitInstallment: 1.75 },
  { max: 110, annual: 20, sixMonth: null, monthlyDirectDebitTotal: 21, monthlyDirectDebitInstallment: 1.75 },
  { max: 120, annual: 35, sixMonth: null, monthlyDirectDebitTotal: 36.75, monthlyDirectDebitInstallment: 3.0625 },
  { max: 130, annual: 170, sixMonth: 93.5, monthlyDirectDebitTotal: 178.5, monthlyDirectDebitInstallment: 14.875 },
  { max: 140, annual: 200, sixMonth: 110, monthlyDirectDebitTotal: 210, monthlyDirectDebitInstallment: 17.5 },
  { max: 150, annual: 225, sixMonth: 123.75, monthlyDirectDebitTotal: 236.25, monthlyDirectDebitInstallment: 19.6875 },
  { max: 165, annual: 275, sixMonth: 151.25, monthlyDirectDebitTotal: 288.75, monthlyDirectDebitInstallment: 24.0625 },
  { max: 175, annual: 325, sixMonth: 178.75, monthlyDirectDebitTotal: 341.25, monthlyDirectDebitInstallment: 28.4375 },
  { max: 185, annual: 360, sixMonth: 198, monthlyDirectDebitTotal: 378, monthlyDirectDebitInstallment: 31.5 },
  { max: 200, annual: 410, sixMonth: 225.5, monthlyDirectDebitTotal: 430.5, monthlyDirectDebitInstallment: 35.875 },
  { max: 225, annual: 445, sixMonth: 244.75, monthlyDirectDebitTotal: 467.25, monthlyDirectDebitInstallment: 38.9375 },
  { max: 255, annual: 760, sixMonth: 418, monthlyDirectDebitTotal: 798, monthlyDirectDebitInstallment: 66.5 },
  { max: Infinity, annual: 790, sixMonth: 434.5, monthlyDirectDebitTotal: 829.5, monthlyDirectDebitInstallment: 69.125 },
] as const;

export const SHARED_MOT_GUIDE_COST = 50;
export const SHARED_MOT_MAX_FEE = 54.85;

function parseFirstRegistrationMonth(value: string | null | undefined): FirstRegistrationMonth | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

function inferFirstRegistrationMonthFromRegistration(
  registrationNumber: string | null | undefined,
): FirstRegistrationMonth | null {
  const normalized = normalizeVehicleRegistrationForMatch(registrationNumber).replace(/\s+/g, '');
  const match = /^([A-Z]{2})(\d{2})([A-Z]{3})$/.exec(normalized);
  if (!match) return null;

  const ageIdentifier = Number(match[2]);
  if (!Number.isInteger(ageIdentifier) || ageIdentifier < 1) {
    return null;
  }

  if (ageIdentifier <= 50) {
    return {
      year: 2000 + ageIdentifier,
      month: 3,
    };
  }

  if (ageIdentifier <= 99) {
    return {
      year: 2000 + (ageIdentifier - 50),
      month: 9,
    };
  }

  return null;
}

function isOnOrAfter(value: FirstRegistrationMonth, year: number, month: number) {
  return value.year > year || (value.year === year && value.month >= month);
}

export function estimateVehicleTaxFromLookup(
  lookup: SharedDvlaVehicleLookupResult,
  registrationNumber?: string | null,
): SharedVehicleTaxEstimate | null {
  const firstRegistrationMonth =
    parseFirstRegistrationMonth(lookup.monthOfFirstRegistration)
    ?? inferFirstRegistrationMonthFromRegistration(registrationNumber);

  if (firstRegistrationMonth && isOnOrAfter(firstRegistrationMonth, 2017, 4)) {
    return {
      ...POST_2017_STANDARD_RATES,
      basis: 'post-2017-standard',
      note: lookup.monthOfFirstRegistration
        ? 'Standard post-April 2017 rate. The old first-five-years expensive-car supplement no longer applies once that window has ended.'
        : 'Standard post-April 2017 rate, inferred from the UK age identifier in the registration number.',
    };
  }

  if (firstRegistrationMonth && isOnOrAfter(firstRegistrationMonth, 2001, 3) && typeof lookup.co2Emissions === 'number') {
    const co2Emissions = lookup.co2Emissions;
    const band = CO2_RATES_2001_TO_2017.find((entry) => co2Emissions <= entry.max);
    if (!band) return null;
    return {
      annual: band.annual,
      sixMonth: band.sixMonth,
      monthlyDirectDebitTotal: band.monthlyDirectDebitTotal,
      monthlyDirectDebitInstallment: band.monthlyDirectDebitInstallment,
      basis: '2001-2017-co2',
      note: 'Estimated from the current pre-April 2017 CO2 band rates.',
    };
  }

  if (firstRegistrationMonth && !isOnOrAfter(firstRegistrationMonth, 2001, 3) && typeof lookup.engineCapacity === 'number') {
    const rates = lookup.engineCapacity <= 1549 ? PRE_2001_ENGINE_RATES.under1550 : PRE_2001_ENGINE_RATES.over1550;
    return {
      ...rates,
      basis: 'pre-2001-engine',
      note: 'Estimated from the current pre-March 2001 engine-size rates.',
    };
  }

  return {
    annual: null,
    sixMonth: null,
    monthlyDirectDebitTotal: null,
    monthlyDirectDebitInstallment: null,
    basis: 'unknown',
    note: 'Adulting could not safely estimate the current vehicle-tax amount for this vehicle from the data available.',
  };
}

export function deriveVehicleServiceDueDate(lastServiceDate: string | null, explicitNextDueDate: string | null) {
  if (explicitNextDueDate) return explicitNextDueDate;
  if (!lastServiceDate) return null;
  try {
    return format(addYears(parseISO(lastServiceDate), 1), 'yyyy-MM-dd');
  } catch {
    return null;
  }
}

export function normalizeVehicleRegistrationForMatch(value: string | null | undefined) {
  return value?.trim().toUpperCase().replace(/\s+/g, ' ') || '';
}

export function findVehicleInsuranceRenewal(
  renewals: SharedVehicleInsuranceRenewal[],
  registrationNumber: string | null | undefined,
) {
  const normalizedRegistration = normalizeVehicleRegistrationForMatch(registrationNumber);
  if (!normalizedRegistration) return null;
  return renewals
    .filter(
      (renewal) =>
        renewal.type === 'car-insurance'
        && normalizeVehicleRegistrationForMatch(renewal.registrationNumber) === normalizedRegistration,
    )
    .sort((left, right) => left.renewalDate.localeCompare(right.renewalDate))[0] || null;
}

export function hasMissingVehicleTaxEstimate(vehicle: SharedHomeVehicleRecord) {
  return (
    vehicle.taxAnnualCost == null
    && vehicle.taxSixMonthCost == null
    && vehicle.taxMonthlyDirectDebitTotal == null
    && vehicle.taxMonthlyDirectDebitInstallment == null
  );
}

export function needsSharedVehicleDerivedFieldRepair(vehicle: SharedHomeVehicleRecord) {
  return (
    vehicle.motEstimatedCost == null
    || (Boolean(vehicle.serviceLastDate) && !vehicle.serviceDueDate)
    || hasMissingVehicleTaxEstimate(vehicle)
  );
}

export function resolveSharedHomeVehicleDerivedFields(
  vehicle: SharedHomeVehicleRecord,
  lookup: SharedDvlaVehicleLookupResult | null = null,
): SharedHomeVehicleRecord {
  const estimatedTax = lookup ? estimateVehicleTaxFromLookup(lookup, vehicle.registrationNumber) : null;
  const serviceDueDate = deriveVehicleServiceDueDate(vehicle.serviceLastDate, vehicle.serviceDueDate);

  return {
    ...vehicle,
    registrationNumber: normalizeVehicleRegistrationForMatch(vehicle.registrationNumber),
    make: lookup?.make ?? vehicle.make ?? null,
    motDueDate: lookup?.motExpiryDate ?? vehicle.motDueDate ?? null,
    motStatus: lookup?.motStatus ?? vehicle.motStatus ?? null,
    taxDueDate: lookup?.taxDueDate ?? vehicle.taxDueDate ?? null,
    taxStatus: lookup?.taxStatus ?? vehicle.taxStatus ?? null,
    taxAnnualCost: estimatedTax?.annual ?? vehicle.taxAnnualCost ?? null,
    taxSixMonthCost: estimatedTax?.sixMonth ?? vehicle.taxSixMonthCost ?? null,
    taxMonthlyDirectDebitTotal:
      estimatedTax?.monthlyDirectDebitTotal ?? vehicle.taxMonthlyDirectDebitTotal ?? null,
    taxMonthlyDirectDebitInstallment:
      estimatedTax?.monthlyDirectDebitInstallment ?? vehicle.taxMonthlyDirectDebitInstallment ?? null,
    motEstimatedCost: vehicle.motEstimatedCost ?? SHARED_MOT_GUIDE_COST,
    serviceDueDate,
    dvlaSyncedAt: lookup?.generatedAt ?? vehicle.dvlaSyncedAt ?? null,
  };
}
